<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { ref, computed, watch } from 'vue'
import LangSwitch from '../UIComponents/LangSwitch.vue'
import { ActionButton, NavigationButton } from 'monocle-components/internal'
import {
  Bars3Icon,
  ChevronRightIcon,
  ChevronLeftIcon,
  UserCircleIcon,
  XMarkIcon,
} from '@heroicons/vue/24/outline'
import { NuxtLink } from '#components'
import { galleryRoutes } from '~/utils/galleryRoutes'
const { t } = useI18n()
const user = useCurrentUser()
const currentRoute = useRouter().currentRoute
const navbarBreadcrumbs = useNavbarBreadcrumbs()
const rootBreadcrumb = {
  label: 'MONOCLE3D',
  to: galleryRoutes.models(),
}

const breadcrumbs = computed(() => {
  if (
    navbarBreadcrumbs.value.sourcePath === currentRoute.value.fullPath &&
    navbarBreadcrumbs.value.items.length
  ) {
    return navbarBreadcrumbs.value.items
  }

  const path = currentRoute.value.path
  let currentLabel = ''
  if (path.startsWith('/account')) {
    currentLabel = t('navbar.breadcrumb.account')
  } else if (path.startsWith('/learn')) {
    currentLabel = t('navbar.breadcrumb.learn')
  } else if (path.startsWith('/settings')) {
    currentLabel = t('navbar.breadcrumb.settings')
  } else if (path.startsWith('/admin')) {
    currentLabel = t('navbar.breadcrumb.admin')
  }

  return currentLabel
    ? [rootBreadcrumb, { label: currentLabel }]
    : [{ label: rootBreadcrumb.label }]
})

const menuActive = ref(false)
const menuToggle = () => {
  menuActive.value = !menuActive.value
}

const closeMenu = () => {
  menuActive.value = false
}

watch(
  () => currentRoute.value.fullPath,
  () => closeMenu(),
)

const isOnAuthPage = computed(() => {
  return (
    currentRoute.value.path === '/signin' ||
    currentRoute.value.path === '/register'
  )
})

const isOnConfirmEmailPage = computed(() => {
  return currentRoute.value.path === '/confirm-email'
})

const { signOutUser } = useAuth()

const handleSignOut = async () => {
  await signOutUser()
}
</script>

<template>
  <nav
    class="navbar"
    role="navigation"
    aria-label="main navigation">
    <div class="navbar-container">
      <div class="navbar-brand">
        <NuxtLink
          :to="galleryRoutes.models()"
          class="navbar-logo">
          <svg
            width="32"
            height="32"
            viewBox="0 0 32 32"
            fill="none"
            xmlns="http://www.w3.org/2000/svg">
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M5.56047 0C6.21031 -1.42027e-08 6.83411 0.11126 7.41379 0.315735C4.34373 1.83977 1.84324 4.3356 0.316449 7.39988C0.111513 6.82122 0 6.19851 0 5.54981C0 2.48473 2.48951 6.7118e-08 5.56047 0ZM16.1396 27.9052C22.645 27.9052 27.9053 22.6437 27.9053 16.1699C27.9053 9.69614 22.645 4.43467 16.1396 4.43467C9.63415 4.43467 4.37389 9.69614 4.37389 16.1699C4.37389 22.6437 9.63415 27.9052 16.1396 27.9052ZM16.1396 32C24.8991 32 32.0001 24.9127 32.0001 16.1699C32.0001 7.42721 24.8991 0.339838 16.1396 0.339838C7.38005 0.339838 0.279053 7.42721 0.279053 16.1699C0.279053 24.9127 7.38005 32 16.1396 32ZM25.1042 13.0774C23.7848 19.0427 19.0792 23.739 13.1023 25.0555C14.0482 25.3715 15.0606 25.5427 16.113 25.5427C21.3482 25.5427 25.5921 21.3069 25.5921 16.0817C25.5921 15.0315 25.4207 14.0213 25.1042 13.0774Z" />
          </svg>
        </NuxtLink>
      </div>
      <div
        v-if="user !== null"
        class="navbar-start">
        <nav
          class="breadcrumb-nav"
          :aria-label="t('navbar.breadcrumb.label')">
          <ol
            class="breadcrumb-list"
            :class="{ 'has-ancestors': breadcrumbs.length > 2 }">
            <li
              v-for="(breadcrumb, index) in breadcrumbs"
              :key="`${index}-${breadcrumb.label}`"
              class="breadcrumb-item"
              :class="{
                'is-root': index === 0,
                'is-parent': index === breadcrumbs.length - 2,
                'is-current': index === breadcrumbs.length - 1,
              }">
              <ChevronRightIcon
                v-if="index > 0"
                class="chevron forward-chevron" />
              <ChevronLeftIcon
                v-if="index > 0 && index === breadcrumbs.length - 2"
                class="chevron back-chevron" />
              <NuxtLink
                v-if="breadcrumb.to"
                class="breadcrumb-label"
                :title="breadcrumb.label"
                :to="breadcrumb.to">
                {{ breadcrumb.label }}
              </NuxtLink>
              <span
                v-else
                class="breadcrumb-label"
                :title="breadcrumb.label"
                aria-current="page">
                {{ breadcrumb.label }}
              </span>
            </li>
          </ol>
        </nav>
      </div>
      <div class="navbar-menu">
        <div class="navbar-end">
          <LangSwitch />
          <NuxtLink
            to="/learn"
            class="nav-icon"
            ><svg
              width="49"
              height="49"
              viewBox="0 0 49 49"
              fill="none"
              xmlns="http://www.w3.org/2000/svg">
              <path
                d="M4.69287 6.14258H16.6929C18.8146 6.14258 20.8494 6.98543 22.3497 8.48572C23.85 9.98601 24.6929 12.0208 24.6929 14.1426V42.1426C24.6929 40.5513 24.0607 39.0252 22.9355 37.8999C21.8103 36.7747 20.2842 36.1426 18.6929 36.1426H4.69287V6.14258Z"
                stroke="currentColor"
                stroke-width="4"
                stroke-linecap="round"
                stroke-linejoin="round" />
              <path
                d="M44.6929 6.14258H32.6929C30.5711 6.14258 28.5363 6.98543 27.036 8.48572C25.5357 9.98601 24.6929 12.0208 24.6929 14.1426V42.1426C24.6929 40.5513 25.325 39.0252 26.4502 37.8999C27.5754 36.7747 29.1016 36.1426 30.6929 36.1426H44.6929V6.14258Z"
                stroke="currentColor"
                stroke-width="4"
                stroke-linecap="round"
                stroke-linejoin="round" />
            </svg>
          </NuxtLink>
          <NuxtLink
            :to="isOnConfirmEmailPage ? '#' : '/account'"
            class="showOnMobile account-icon-wrapper"
            v-if="user !== null">
            <UserCircleIcon class="account-icon" />
          </NuxtLink>
          <NavigationButton
            class="noShowOnMobileInline"
            v-if="user !== null && !isOnConfirmEmailPage"
            link="/account"
            :label="t('navbar.links.account')"
            @click.native="
              () => {
                closeMenu()
              }
            " />
          <ActionButton
            class="noShowOnMobileInline sign-out-nav-button"
            v-if="user !== null && isOnConfirmEmailPage"
            size="sm"
            @click="handleSignOut"
            :label="t('navbar.auth.signOut')" />

          <div
            class="sign-in-button-wrapper"
            v-if="user === null && !isOnAuthPage">
            <NuxtLink
              class="showOnMobile account-icon-wrapper"
              @click.native="closeMenu"
              to="/signin">
              <UserCircleIcon class="account-icon" />
            </NuxtLink>
            <NavigationButton
              class="noShowOnMobileInline"
              :label="t('navbar.auth.signIn')"
              link="/signin/"
              :primary="true"
              :slide="true"
              @click.native="closeMenu" />
          </div>
        </div>
      </div>

      <button
        type="button"
        class="mobile-menu-toggle"
        aria-controls="mobile-navbar-menu"
        :aria-expanded="menuActive ? 'true' : 'false'"
        :aria-label="
          menuActive ? 'Close navigation menu' : 'Open navigation menu'
        "
        @click="menuToggle">
        <XMarkIcon
          v-if="menuActive"
          class="mobile-menu-toggle-icon" />
        <Bars3Icon
          v-else
          class="mobile-menu-toggle-icon" />
      </button>
    </div>

    <Transition name="mobile-menu">
      <div
        v-if="menuActive"
        id="mobile-navbar-menu"
        class="mobile-menu-panel">
        <div class="mobile-menu-content">
          <div class="mobile-menu-language">
            <span class="mobile-menu-label">
              {{ t('navbar.links.language') }}
            </span>
            <LangSwitch
              class="mobile-language-switch"
              variant="text" />
          </div>

          <NuxtLink
            to="/learn"
            class="mobile-menu-link"
            @click.native="
              () => {
                closeMenu()
              }
            ">
            {{ t('navbar.links.learn') }}
          </NuxtLink>

          <NuxtLink
            v-if="user !== null && !isOnConfirmEmailPage"
            to="/account"
            class="mobile-menu-link"
            @click.native="
              () => {
                closeMenu()
              }
            ">
            {{ t('navbar.links.account') }}
          </NuxtLink>

          <ActionButton
            v-if="user !== null && isOnConfirmEmailPage"
            class="mobile-menu-action"
            size="sm"
            @click="
              () => {
                handleSignOut()
                closeMenu()
              }
            "
            :label="t('navbar.auth.signOut')" />

          <NuxtLink
            v-if="user === null && !isOnAuthPage"
            to="/signin"
            class="mobile-menu-link"
            @click.native="closeMenu">
            {{ t('navbar.auth.signIn') }}
          </NuxtLink>
        </div>
      </div>
    </Transition>
  </nav>
</template>

<style scoped>
/* Navbar Base */
.navbar {
  width: 100%;
  background-color: var(--content-background);
  color: var(--text-color);
  z-index: var(--z-nav);
  border-bottom: 0.5px solid var(--background-reverse);
  position: relative;
}

.navbar-container {
  margin: 0 auto;
  padding: 0 4.5vw;
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 64px;
  position: relative;
}

/* Brand & Logo */
.navbar-brand {
  height: 100%;
  display: flex;
  align-items: center;
}

.navbar-logo {
  display: flex;
  align-items: center;
  padding-left: 0.5rem;
}

.navbar-logo svg {
  width: 2.5rem;
  height: 2.5rem;
  color: var(--text-color);
  fill: var(--text-color);
  transition: opacity 0.3s ease;
}

.navbar-logo:hover svg {
  opacity: 0.8;
}

/* Menu Structure */
.navbar-menu {
  height: 100%;
  display: none;
  justify-content: flex-end;
  align-items: center;
  flex: 0 0 auto;
  font-family: var(--title-font);
}

.navbar-start {
  display: flex;
  align-items: center;
  flex: 1 1 auto;
  min-width: 0;
  height: 64px;
  margin-left: 1rem;
  font-family: var(--default-font-family);
}

.navbar-end {
  display: flex;
  height: 64px;
  align-items: center;
}

.breadcrumb-nav {
  width: 100%;
  min-width: 0;
  overflow: hidden;
}

.breadcrumb-list {
  display: flex;
  width: 100%;
  min-width: 0;
  list-style: none;
  margin: 0;
  padding: 0;
  align-items: center;
}

.breadcrumb-item {
  display: flex;
  align-items: center;
  min-width: 0;
  max-width: 16rem;
  flex: 0 1 auto;
}

.breadcrumb-item.is-current {
  flex: 1 1 auto;
  max-width: 300px;
}

.breadcrumb-label {
  display: block;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--text-color);
  font-size: 1rem;
  letter-spacing: 0.5px;
  font-weight: 400;
}

.breadcrumb-item a {
  text-decoration: none;
  transition: color 0.3s ease;
}

.breadcrumb-item a:hover {
  color: var(--accent-color);
}

.chevron {
  width: 1rem;
  height: 1rem;
  fill: none;
  stroke: var(--text-color);
  flex-shrink: 0;
  padding: 0.25rem;
}

.back-chevron {
  display: none;
}

.nav-icon {
  width: 2rem;
  display: flex;
  align-items: center;
  fill: var(--text-color);
  transition: opacity 0.3s ease;
  margin: 0rem 2rem;
  transition-duration: 0.3s;
}

.nav-icon svg path {
  transition-duration: 0.3s;
  stroke: var(--text-color);
}

.account-icon-wrapper {
  display: flex;
  height: 100%;
  align-items: center;
  color: var(--text-color);
  text-decoration: none;
  transition: opacity 0.3s ease;
}

.account-icon-wrapper:hover {
  opacity: 0.8;
}

.account-icon {
  width: 3rem;
  height: 3rem;

  fill: none;
  stroke: var(--text-color);
  display: flex;
  align-items: center;
}

.sign-in-button-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  gap: 1rem;
}

.mobile-menu-toggle {
  width: 44px;
  height: 44px;
  display: inline-grid;
  place-items: center;
  flex: 0 0 auto;
  border: none;
  border-radius: 999px;
  background: transparent;
  color: var(--text-color);
  cursor: pointer;
  padding: 0;
}

.mobile-menu-toggle-icon {
  width: 2rem;
  height: 2rem;
}

.mobile-menu-panel {
  position: fixed;
  top: 64px;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: calc(var(--z-nav) + 1);
  background-color: var(--background-color);
  overflow-y: auto;
}

.mobile-menu-content {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  min-height: 100%;
  padding: 1.5rem 4.5vw 2rem;
  box-sizing: border-box;
  font-family: var(--title-font);
}

.mobile-menu-language {
  min-height: 44px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}

.mobile-menu-label {
  color: var(--text-color);
  font-size: 1rem;
}

.mobile-menu-link {
  min-height: 44px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  color: var(--text-color);
  text-decoration: none;
  font-size: 1rem;
}

.mobile-menu-action {
  margin-top: 0.5rem;
  justify-content: center;
}

.mobile-menu-enter-active,
.mobile-menu-leave-active {
  transition:
    opacity 0.16s ease,
    transform 0.16s ease;
}

.mobile-menu-enter-from,
.mobile-menu-leave-to {
  opacity: 0;
  transform: translateY(-0.35rem);
}

.showOnMobile {
  display: block;
  align-content: center;
}

.noShowOnMobileInline {
  display: none;
}

@media (max-width: 767px) {
  .navbar {
    z-index: var(--z-nav);
  }

  .mobile-menu-panel {
    z-index: calc(var(--z-nav) + 1);
  }

  .navbar-container {
    gap: 0.25rem;
    padding: 0 0.75rem;
  }

  .navbar-logo {
    padding-left: 0;
  }

  .navbar-logo svg {
    width: 2rem;
    height: 2rem;
  }

  .navbar-start {
    margin-left: 0.25rem;
  }

  .breadcrumb-item.is-root {
    flex: 0 0 auto;
    max-width: none;
  }

  .breadcrumb-item.is-root .breadcrumb-label {
    overflow: visible;
    text-overflow: clip;
  }

  .breadcrumb-item:not(.is-root) {
    flex: 1 1 auto;
    min-width: 0;
    max-width: none;
  }

  .has-ancestors .breadcrumb-item:not(.is-root):not(.is-parent),
  .has-ancestors .is-parent .forward-chevron {
    display: none;
  }

  .has-ancestors .is-parent .back-chevron {
    display: block;
  }

  .breadcrumb-item a {
    min-height: 44px;
    align-content: center;
  }

  .breadcrumb-label {
    font-size: 0.8rem;
  }

  .chevron {
    width: 0.75rem;
    height: 0.75rem;
    padding: 0.125rem;
  }
}

@media (min-width: 768px) {
  .navbar-menu {
    display: flex;
  }

  .mobile-menu-toggle,
  .mobile-menu-panel {
    display: none;
  }

  .showOnMobile {
    display: none;
  }

  .noShowOnMobileInline {
    display: flex;
  }
}

.sign-out-nav-button {
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
</style>
